import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/services/analytics/analyticsService";
import { BrandLogo } from "@/components/common/BrandLogo";
import { BrandButton } from "@/components/common/BrandButton";
import { PublicContainer } from "@/components/common/PublicContainer";
import { getPublicHeaderConfig } from "@/components/public/publicChrome";

const secondaryBtnClass =
  "border border-zinc-700 text-white rounded-full px-6 py-3 hover:bg-zinc-900 bg-transparent h-auto";

function NavLinks({ className, onNavigate, links }) {
  return (
    <nav className={cn("flex items-center gap-6", className)} aria-label="Navegação principal">
      {links.map(({ label, href }) => (
        <a
          key={href}
          href={href}
          onClick={onNavigate}
          className="text-sm text-zinc-400 hover:text-white transition-colors"
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

function GuestAuthButtons({ className, onNavigate }) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <Button variant="outline" className={secondaryBtnClass} asChild>
        <Link to="/login" onClick={onNavigate} data-testid="landing-header-login-btn">
          Entrar
        </Link>
      </Button>
      <BrandButton asChild>
        <Link
          to="/register"
          onClick={() => {
            onNavigate?.();
            trackEvent("click_cta_start", { cta_location: "header" });
          }}
          data-testid="landing-header-register-btn"
        >
          Começar grátis
        </Link>
      </BrandButton>
    </div>
  );
}

function AuthenticatedAuthButtons({ className, onNavigate }) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <BrandButton asChild>
        <Link
          to="/dashboard"
          onClick={onNavigate}
          data-testid="landing-header-dashboard-btn"
        >
          Ir para o Dashboard
        </Link>
      </BrandButton>
    </div>
  );
}

function AuthButtons({ className, onNavigate, isAuthenticated }) {
  if (isAuthenticated) {
    return (
      <AuthenticatedAuthButtons className={className} onNavigate={onNavigate} />
    );
  }

  return <GuestAuthButtons className={className} onNavigate={onNavigate} />;
}

function DocsActions({
  className,
  onNavigate,
  isAuthenticated,
  navLabel,
  testIdPrefix,
}) {
  return (
    <nav
      className={cn("flex items-center gap-3", className)}
      aria-label={navLabel}
    >
      <Link
        to="/"
        onClick={onNavigate}
        className="text-sm text-zinc-400 hover:text-white transition-colors"
        data-testid={`${testIdPrefix}-back`}
      >
        Voltar ao FIVI360
      </Link>
      {isAuthenticated ? (
        <BrandButton asChild>
          <Link
            to="/dashboard"
            onClick={onNavigate}
            data-testid={`${testIdPrefix}-dashboard`}
          >
            Ir para o Dashboard
          </Link>
        </BrandButton>
      ) : (
        <Button variant="outline" className={secondaryBtnClass} asChild>
          <Link
            to="/login"
            onClick={onNavigate}
            data-testid={`${testIdPrefix}-login`}
          >
            Entrar
          </Link>
        </Button>
      )}
    </nav>
  );
}

function usesDocsActions(actions) {
  return actions === "help" || actions === "docs";
}

/**
 * Chrome institucional do header público.
 *
 * `context="home"` é a referência visual institucional.
 * `legal` e `help` reutilizam o mesmo chrome sem âncoras da Home.
 *
 * `context="legal"` aceita `contextLabel` para identificar o documento
 * (Termos / Privacidade) sem criar contexts adicionais.
 *
 * `useAuth` cobre visitante vs autenticado em home, legal e help.
 * O módulo `src/help/` não importa Auth — só este chrome compartilhado.
 */
export function PublicHeader({
  context = "home",
  contextLabel,
  fixed = false,
  className,
}) {
  const config = getPublicHeaderConfig(context, { contextLabel });
  const showDocsActions = usesDocsActions(config.actions);
  const docsTestIdPrefix =
    config.context === "legal" ? "public-header-legal" : "public-header-help";
  const docsNavLabel =
    config.context === "legal"
      ? "Atalhos das páginas legais"
      : "Atalhos da Central de Ajuda";
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);
  const hasNav = config.navLinks.length > 0;

  return (
    <header
      data-testid="public-header"
      data-chrome-context={config.context}
      className={cn(
        "top-0 z-50 w-full border-b transition-colors duration-200",
        fixed ? "fixed left-0 right-0" : "sticky",
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-zinc-800"
          : "bg-[#050505]/40 border-transparent",
        className,
      )}
    >
      <PublicContainer className="h-16 flex items-center justify-between gap-4">
        <Link
          to={config.brandHref}
          className={cn(
            "inline-flex items-center",
            config.contextLabel ? "gap-3 min-w-0" : "shrink-0",
          )}
          data-testid="landing-logo"
        >
          <BrandLogo className="h-4" />
          {config.contextLabel ? (
            <>
              <span className="hidden h-4 w-px bg-zinc-700 sm:block" aria-hidden />
              <span
                className="truncate text-sm text-zinc-400"
                data-testid="public-header-context-label"
              >
                {config.contextLabel}
              </span>
            </>
          ) : null}
        </Link>

        {hasNav ? <NavLinks className="hidden lg:flex" links={config.navLinks} /> : null}

        <div className="hidden lg:flex">
          {showDocsActions ? (
            <DocsActions
              isAuthenticated={isAuthenticated}
              navLabel={docsNavLabel}
              testIdPrefix={docsTestIdPrefix}
            />
          ) : (
            <AuthButtons isAuthenticated={isAuthenticated} />
          )}
        </div>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-zinc-400 hover:text-white"
              aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={mobileOpen}
              data-testid="landing-mobile-menu-btn"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="bg-[#050505] border-zinc-800 w-full sm:max-w-sm"
            aria-label="Menu de navegação"
          >
            <SheetHeader>
              <SheetTitle className="text-left">
                <BrandLogo className="h-7" />
              </SheetTitle>
            </SheetHeader>
            <div className="mt-8 flex flex-col gap-6">
              {hasNav ? (
                <NavLinks
                  className="flex-col items-start gap-4"
                  onNavigate={closeMobile}
                  links={config.navLinks}
                />
              ) : null}
              {showDocsActions ? (
                <DocsActions
                  className="flex-col w-full items-start gap-3"
                  onNavigate={closeMobile}
                  isAuthenticated={isAuthenticated}
                  navLabel={docsNavLabel}
                  testIdPrefix={docsTestIdPrefix}
                />
              ) : (
                <AuthButtons
                  className="flex-col w-full gap-3"
                  onNavigate={closeMobile}
                  isAuthenticated={isAuthenticated}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      </PublicContainer>
    </header>
  );
}
